import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Divider, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import PayPalProviderStatus from './PayPalProviderStatus';

const money = value => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(value || 0));
const date = value => value ? new Date(value).toLocaleString('it-IT', { timeZone: 'Europe/Rome' }) : 'Non ricevuto';
const notificationName = type => ({ 'CHECKOUT.ORDER.APPROVED': 'Ordine approvato',
  'PAYMENT.CAPTURE.COMPLETED': 'Incasso completato', 'PAYMENT.CAPTURE.PENDING': 'Incasso in attesa',
  'PAYMENT.CAPTURE.DENIED': 'Incasso rifiutato', 'PAYMENT.CAPTURE.REFUNDED': 'Rimborso',
  'PAYMENT.CAPTURE.REVERSED': 'Storno' }[type] || type);

export default function PayPalPaymentAudit({ invoiceId }) {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const queryClient = useQueryClient();
  const previous = useRef('');
  const { data = [], isFetching, error, refetch } = useQuery({
    queryKey: ['paypal-invoice-attempts', invoiceId],
    queryFn: async () => (await api.get(`/paypal/invoices/${invoiceId}/attempts`)).data.data,
    enabled: user?.ruolo === 'admin' && expanded, retry: false,
    refetchInterval: expanded ? 10000 : false, refetchOnWindowFocus: true,
  });
  useEffect(() => {
    const signature = data.filter(row => row.environment === 'live' && row.accounting_recorded)
      .map(row => `${row.id}:${row.state}:${row.accounting_amount}`).join('|');
    if (signature && previous.current !== signature) {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoicePaymentsHistory', invoiceId] });
    }
    previous.current = signature;
  }, [data, invoiceId, queryClient]);
  if (user?.ruolo !== 'admin') return null;
  return <Accordion expanded={expanded} onChange={(_, value) => setExpanded(value)} sx={{ my: 2 }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">Verifiche pagamenti PayPal</Typography></AccordionSummary>
    <AccordionDetails>
      <Button onClick={() => refetch()} disabled={isFetching} sx={{ mb: 2 }}>{isFetching ? 'Aggiornamento…' : 'Aggiorna esiti'}</Button>
      {error && <Alert severity="error">{error.response?.data?.message || 'Verifiche PayPal non disponibili.'}</Alert>}
      {!error && !isFetching && !data.length && <Alert severity="info">Nessun tentativo PayPal presente per questa fattura.</Alert>}
      {data.map((row, index) => <Box key={row.id} sx={{ mb: 3, overflowWrap: 'anywhere' }}>
        {index > 0 && <Divider sx={{ mb: 2 }} />}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
          <Chip size="small" label={row.environment === 'sandbox' ? 'Sandbox · simulazione' : 'Live · pagamento reale'} />
          <Chip size="small" label={row.label} color={row.completed ? 'success' : 'warning'} />
          {row.confirmation_mode === 'webhook_only' && <Chip size="small" color="info" label="Collaudo: solo notifiche" />}
          <Typography fontWeight="bold">{money(row.amount)}</Typography>
        </Box>
        <Typography>{row.description}</Typography>
        <Typography variant="body2" color="text.secondary">Tentativo: {date(row.created_at)}</Typography>
        <Typography variant="body2">Ordine PayPal: {row.provider_order_id || 'Creazione da verificare'}</Typography>
        <Typography variant="body2">Codice transazione: {row.capture_id || 'Non ancora disponibile'}</Typography>
        <Typography variant="body2">Conferma dal browser: {date(row.returned_at)}</Typography>
        {row.captured_at && <Typography variant="body2">Data PayPal: {date(row.captured_at)}</Typography>}
        {Number(row.refunded_amount) > 0 && <Typography variant="body2">Rimborsato: {money(row.refunded_amount)}</Typography>}
        <Alert severity={row.environment === 'sandbox' ? 'info' : row.accounting_recorded ? 'success' : 'warning'} sx={{ my: 1 }}>
          {row.environment === 'sandbox' ? 'Prova Sandbox: nessun movimento contabile e saldo fattura invariato.' :
            row.accounting_recorded ? `Incasso registrato. Saldo contabile di questo ordine, al netto degli storni: ${money(row.accounting_amount)}.` :
              'Nessun incasso contabile registrato: conferma del pagamento in attesa.'}
        </Alert>
        {row.review_required && <Alert severity="warning" sx={{ mb: 1 }}>Operazione da verificare con i dati aggiornati della fattura.</Alert>}
        {row.refund_pending && <Alert severity="warning" sx={{ mb: 1 }}>PayPal segnala un rimborso. Importi registrati in aggiornamento, in attesa delle notifiche di dettaglio.</Alert>}
        {row.provider_order_id && <PayPalProviderStatus key={`${invoiceId}:${row.id}`} invoiceId={invoiceId} attemptId={row.id} />}
        <Typography variant="body2" fontWeight="bold">Notifiche PayPal verificate: {row.notifications.length}</Typography>
        {!row.notifications.length && <Typography variant="body2" color="text.secondary">Nessuna notifica ancora elaborata.</Typography>}
        {row.notifications.map(event => <Typography key={event.id} variant="body2">
          {notificationName(event.type)} · {date(event.received_at)} · {event.id}
        </Typography>)}
      </Box>)}
    </AccordionDetails>
  </Accordion>;
}
